import type {
  OrganizationRoleValue,
  ProjectRoleValue,
  RecipientType,
  TeamMember,
} from '../../containers/SendEmailContainer/types';
import type {
  BuildRecipientOptions,
  RecipientMember,
  RecipientOption,
} from './types';

const MANAGER_ROLE: ProjectRoleValue = 'manager';
const ADMIN_ROLE: OrganizationRoleValue = 'admin';

const formatMemberLabel = (member: RecipientMember): string => {
  const fullName = [member.firstName, member.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName ? `${fullName} <${member.email}>` : member.email;
};

const projectRoleType = (member: TeamMember): RecipientType =>
  member.projectRole === MANAGER_ROLE ? 'manager' : 'member';

// Recipients ranked most-relevant first: the creator, then organization admins
// (who can access any project, so they count as implicit members), then project
// managers and members. Deduplicated by email — the highest-ranked type wins.
export const buildRecipientOptions: BuildRecipientOptions = ({
  creatorMember,
  teamMembers,
  availableMembers,
}) => {
  const admins = [...teamMembers, ...availableMembers].filter(
    (member) => member.organizationRole === ADMIN_ROLE,
  );

  const ranked: Array<{ member: RecipientMember; type: RecipientType }> = [
    ...(creatorMember ? [{ member: creatorMember, type: 'creator' as const }] : []),
    ...admins.map((member) => ({ member, type: 'admin' as const })),
    ...teamMembers.map((member) => ({ member, type: projectRoleType(member) })),
  ];

  const options: RecipientOption[] = [];
  const seenEmails = new Set<string>();

  for (const { member, type } of ranked) {
    const email = member.email.trim();
    const normalizedEmail = email.toLowerCase();
    if (!email || seenEmails.has(normalizedEmail)) continue;
    seenEmails.add(normalizedEmail);
    options.push({
      key: `${type}-${member.id}`,
      email,
      label: `${formatMemberLabel(member)} — ${type}`,
      type,
    });
  }

  return options;
};
